import React, { useState } from "react";
import {
  Row,
  Col,
  Typography,
  Input,
  Table,
  Tag,
  Image,
  Space,
  Button,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";

const { Title, Text } = Typography;

interface ReimbursementData {
  key: string;
  status: string;
  customerIncentiveCode: string;
  reimbursementAmount: string;
  eBikeIncentiveAmount: string;
  safetyGearAmount: string;
  customerName: string;
  serialNumber: string;
  dateOfSale: string;
  submittedBy: string;
}

const ReimbursementRequest: React.FC = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);

  const data: ReimbursementData[] = [
    {
      key: "1",
      status: "pending",
      customerIncentiveCode: "5ds6d",
      reimbursementAmount: "$1,100.00",
      eBikeIncentiveAmount: "$900",
      safetyGearAmount: "$200",
      customerName: "Brett Ludwig",
      serialNumber: "777136",
      dateOfSale: "01/22/2025",
      submittedBy: "Eduardo Tolentino",
    },
    {
      key: "2",
      status: "pending",
      customerIncentiveCode: "688dd4",
      reimbursementAmount: "$500.00",
      eBikeIncentiveAmount: "$400",
      safetyGearAmount: "$100",
      customerName: "Meghan Clark",
      serialNumber: "896792",
      dateOfSale: "01/22/2025",
      submittedBy: "Eduardo Tolentino",
    },
    {
      key: "3",
      status: "pending",
      customerIncentiveCode: "5ds6jd",
      reimbursementAmount: "$1,100.00",
      eBikeIncentiveAmount: "$900",
      safetyGearAmount: "$200",
      customerName: "Garrett Dyer",
      serialNumber: "777136",
      dateOfSale: "01/21/2025",
      submittedBy: "Justin Galioto",
    },
    {
      key: "4",
      status: "approved",
      customerIncentiveCode: "22jmdd",
      reimbursementAmount: "$1,100.00",
      eBikeIncentiveAmount: "$900",
      safetyGearAmount: "$200",
      customerName: "Riley Denton",
      serialNumber: "896792",
      dateOfSale: "01/21/2025",
      submittedBy: "Samantha Fryer",
    },
    {
      key: "5",
      status: "approved",
      customerIncentiveCode: "5ds6dj",
      reimbursementAmount: "$500.00",
      eBikeIncentiveAmount: "$400",
      safetyGearAmount: "$100",
      customerName: "Richard Wagner",
      serialNumber: "777136",
      dateOfSale: "01/21/2025",
      submittedBy: "Andrew Sutton",
    },
    {
      key: "6",
      status: "funded",
      customerIncentiveCode: "17dqt9",
      reimbursementAmount: "$500.00",
      eBikeIncentiveAmount: "$400",
      safetyGearAmount: "$100",
      customerName: "Elizabeth Wingen",
      serialNumber: "896792",
      dateOfSale: "01/21/2025",
      submittedBy: "Justin Galioto",
    },
    {
      key: "7",
      status: "funded",
      customerIncentiveCode: "xmo082",
      reimbursementAmount: "$1,500.00",
      eBikeIncentiveAmount: "$1,200",
      safetyGearAmount: "$300",
      customerName: "Sara Shillman",
      serialNumber: "777136",
      dateOfSale: "01/11/2025",
      submittedBy: "Andrew Sutton",
    },
    {
      key: "8",
      status: "funded",
      customerIncentiveCode: "5ds6dd",
      reimbursementAmount: "$1,100.00",
      eBikeIncentiveAmount: "$200",
      safetyGearAmount: "$200",
      customerName: "Christian Diehl",
      serialNumber: "777136",
      dateOfSale: "01/11/2025",
      submittedBy: "Samantha Fryer",
    },
    {
      key: "9",
      status: "funded",
      customerIncentiveCode: "733dds",
      reimbursementAmount: "$1,100.00",
      eBikeIncentiveAmount: "$900",
      safetyGearAmount: "$200",
      customerName: "Jose Viera",
      serialNumber: "896792",
      dateOfSale: "01/11/2025",
      submittedBy: "Andrew Sutton",
    },
    {
      key: "10",
      status: "declined",
      customerIncentiveCode: "1s5d6f",
      reimbursementAmount: "$1,500.00",
      eBikeIncentiveAmount: "$1,200",
      safetyGearAmount: "$300",
      customerName: "Hollee Guthrie",
      serialNumber: "896792",
      dateOfSale: "01/11/2025",
      submittedBy: "Eduardo Tolentino",
    },
  ];

  const getStatusTag = (status: string) => {
    const statusConfig = {
      pending: {
        color: "rgba(215,143,20,1)",
        backgroundColor: "#FFFBE6",
        text: "Pending",
      },
      approved: {
        color: "rgba(36,107,222,1)",
        backgroundColor: "#E6F4FF",
        text: "Approved",
      },
      funded: {
        color: "rgba(56,158,13,1)",
        backgroundColor: "#F6FFED",
        text: "Funded",
      },
      declined: {
        color: "rgba(68,68,68,1)",
        backgroundColor: "#EEE",
        text: "Declined",
      },
    };

    const config = statusConfig[status as keyof typeof statusConfig];
    return (
      <Tag
        style={{
          color: config.color,
          backgroundColor: config.backgroundColor,
          border: "none",
          borderRadius: "8px",
          padding: "8px",
          fontSize: "16px",
          fontWeight: "400",
          lineHeight: "16px",
          fontFamily: "Open Sans, -apple-system, Roboto, Helvetica, sans-serif",
        }}
      >
        {config.text}
      </Tag>
    );
  };

  const columns = [
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 150,
      render: (status: string) => getStatusTag(status),
    },
    {
      title: "Customer Incentive Code",
      dataIndex: "customerIncentiveCode",
      key: "customerIncentiveCode",
      width: 200,
    },
    {
      title: "Reimbursement Amount",
      dataIndex: "reimbursementAmount",
      key: "reimbursementAmount",
      width: 180,
    },
    {
      title: "E-Bike Incentive Amount",
      dataIndex: "eBikeIncentiveAmount",
      key: "eBikeIncentiveAmount",
      width: 180,
    },
    {
      title: "Safety Gear Amount",
      dataIndex: "safetyGearAmount",
      key: "safetyGearAmount",
      width: 160,
    },
    {
      title: "Customer Name",
      dataIndex: "customerName",
      key: "customerName",
      width: 160,
    },
    {
      title: "Serial Number",
      dataIndex: "serialNumber",
      key: "serialNumber",
      width: 140,
    },
    {
      title: "Date of Sale",
      dataIndex: "dateOfSale",
      key: "dateOfSale",
      width: 140,
    },
    {
      title: "Submitted By",
      dataIndex: "submittedBy",
      key: "submittedBy",
      width: 160,
    },
  ];

  const filteredData = data.filter((item) => {
    if (activeTab === "all") return true;
    return item.status === activeTab;
  });

  const tabs = [
    {
      key: "all",
      label: "All Requests",
      icon: "/images/all-requests-icon.svg",
      iconColor: "#007AFF",
    },
    {
      key: "pending",
      label: "Pending",
      icon: "/images/pending-status-icon.svg",
      iconColor: "#AAAAAA",
    },
    {
      key: "approved",
      label: "Approved",
      icon: "/images/approved-status-icon.svg",
      iconColor: "#AAAAAA",
    },
    {
      key: "funded",
      label: "Funded",
      icon: "/images/funded-status-icon.svg",
      iconColor: "#AAAAAA",
    },
    {
      key: "declined",
      label: "Declined",
      icon: "/images/declined-status-icon.svg",
      iconColor: "#AAAAAA",
    },
  ];

  return (
    <div
      style={{
        flex: 1,
        backgroundColor: "#FAFAFA",
        padding: "119px 20px 20px 20px",
        minHeight: "100vh",
      }}
    >
      {/* Page Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "20px",
          marginBottom: "40px",
        }}
      >
        <div
          style={{
            width: "36px",
            height: "36px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Image
            src="/images/reimbursement-icon.svg"
            alt="Reimbursement"
            width={30}
            height={31}
            preview={false}
          />
        </div>
        <Title
          level={2}
          style={{
            margin: 0,
            color: "#4D4D4D",
            fontSize: "24px",
            fontWeight: "700",
            lineHeight: "24px",
            letterSpacing: "0.5px",
            fontFamily:
              "Open Sans, -apple-system, Roboto, Helvetica, sans-serif",
          }}
        >
          Reimbursement Requests
        </Title>
      </div>

      {/* Filter Tabs */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "30px",
          marginBottom: "40px",
          borderBottom: "1px solid #CCC",
          paddingBottom: "5px",
          overflowX: "auto",
        }}
      >
        {tabs.map((tab) => (
          <div
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              paddingBottom: "22px",
              position: "relative",
              borderBottom:
                activeTab === tab.key ? "2px solid #007AFF" : "none",
              whiteSpace: "nowrap",
            }}
          >
            <div
              style={{
                width: "18px",
                height: "18px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Image
                src={tab.icon}
                alt={tab.label}
                width={16}
                height={16}
                preview={false}
                style={{
                  filter:
                    activeTab === tab.key && tab.key === "all"
                      ? "none"
                      : "grayscale(1)",
                }}
              />
            </div>
            <Text
              style={{
                color: activeTab === tab.key ? "#007AFF" : "#AAA",
                fontSize: "16px",
                fontWeight: activeTab === tab.key ? "700" : "400",
                lineHeight: "16px",
                letterSpacing: "0.5px",
                fontFamily:
                  "Open Sans, -apple-system, Roboto, Helvetica, sans-serif",
              }}
            >
              {tab.label}
            </Text>
          </div>
        ))}
      </div>

      {/* Search and Count */}
      <Row
        justify="space-between"
        align="middle"
        style={{ marginBottom: "20px" }}
        gutter={[16, 16]}
      >
        <Col xs={24} lg={12}>
          <Text
            style={{
              color: "#4D4D4D",
              fontSize: "16px",
              fontWeight: "700",
              lineHeight: "24px",
              letterSpacing: "0.5px",
              fontFamily:
                "Open Sans, -apple-system, Roboto, Helvetica, sans-serif",
            }}
          >
            11 Reimbursement Requests Submitted
          </Text>
        </Col>
        <Col xs={24} lg={12}>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Input
              placeholder="Search"
              prefix={<SearchOutlined style={{ color: "#AAA" }} />}
              style={{
                width: "292px",
                height: "36px",
                borderRadius: "6px",
                border: "1px solid #CCC",
                backgroundColor: "#FFF",
                maxWidth: "100%",
              }}
            />
          </div>
        </Col>
      </Row>

      {/* Table */}
      <div
        style={{
          width: "100%",
          borderRadius: "10px",
          boxShadow: "0px 2px 4px 0px rgba(0, 0, 0, 0.20)",
          marginBottom: "40px",
          overflow: "hidden",
          backgroundColor: "#FFF",
        }}
      >
        <Table
          columns={columns}
          dataSource={filteredData}
          pagination={false}
          scroll={{ x: 1550 }}
          className="reimbursement-table"
          style={{
            backgroundColor: "#FFF",
          }}
        />
      </div>

      {/* Pagination */}
      <Row justify="center" style={{ marginBottom: "40px" }}>
        <Space size="large" align="center">
          <Button
            type="text"
            icon={
              <Image
                src="/images/chevron-left.svg"
                alt="Previous"
                width={28}
                height={28}
                preview={false}
              />
            }
            style={{
              border: "none",
              padding: 0,
              background: "transparent",
            }}
          />
          <Button
            type="text"
            style={{
              color: "rgba(36,107,222,1)",
              fontSize: "18px",
              fontWeight: "400",
              lineHeight: "18px",
              borderRadius: "4px",
              border: "1px solid #E6F4FF",
              padding: "10px 14px",
              fontFamily:
                "Open Sans, -apple-system, Roboto, Helvetica, sans-serif",
            }}
          >
            1
          </Button>
          <Button
            type="text"
            style={{
              color: "#4D4D4D",
              fontSize: "18px",
              fontWeight: "400",
              lineHeight: "18px",
              padding: "10px 14px",
              fontFamily:
                "Open Sans, -apple-system, Roboto, Helvetica, sans-serif",
            }}
          >
            2
          </Button>
          <Button
            type="text"
            style={{
              color: "#4D4D4D",
              fontSize: "18px",
              fontWeight: "400",
              lineHeight: "18px",
              padding: "10px 14px",
              fontFamily:
                "Open Sans, -apple-system, Roboto, Helvetica, sans-serif",
            }}
          >
            3
          </Button>
          <Button
            type="text"
            style={{
              color: "#4D4D4D",
              fontSize: "18px",
              fontWeight: "400",
              lineHeight: "18px",
              padding: "10px 14px",
              fontFamily:
                "Open Sans, -apple-system, Roboto, Helvetica, sans-serif",
            }}
          >
            4
          </Button>
          <Button
            type="text"
            icon={
              <Image
                src="/images/chevron-right.svg"
                alt="Next"
                width={28}
                height={28}
                preview={false}
              />
            }
            style={{
              border: "none",
              padding: 0,
              background: "transparent",
            }}
          />
        </Space>
      </Row>
    </div>
  );
};

export default ReimbursementRequest;
